/**
 * LAYOUT v4.2
 * ✅ Todo de v4.0 preservado
 * ✅ Componentes restaurado en barra principal
 * ✅ CookablePage → "¿Qué cocino?" en menú secundario
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFirebase } from '../context/FirebaseContext';
import { useTutorial } from '../context/TutorialContext';
import LoginModal from './LoginModal';
import HouseholdSetup from './HouseholdSetup';
import ThemeToggle from './ThemeToggle';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const { isSyncing, household, syncEnabled, userProfile } = useFirebase();
  const { resetTutorial } = useTutorial();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showHouseholdSetup, setShowHouseholdSetup] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // ✅ Componentes restaurado en barra principal, CookablePage accesible desde Planner
  const navItems = useMemo(() => [
    { path: '/', label: 'Stock', icon: '📦' },
    { path: '/components', label: 'Componentes', icon: '🧩' },
    { path: '/planner', label: 'Plan', icon: '📅' },
    { path: '/shopping', label: 'Compras', icon: '🛒' },
    { path: '/recipes', label: 'Recetas', icon: '📖' },
  ], []);

  const secondaryNav = useMemo(() => [
    { path: '/cookable', label: '¿Qué cocino?', icon: '🔥' },
    { path: '/family', label: 'Familia', icon: '👨‍👩‍👧‍👦' },
    { path: '/stats', label: 'Estadísticas', icon: '📊' },
    { path: '/notifications', label: 'Notificaciones', icon: '🔔' }
  ], []);

  const handleSyncClick = useCallback(() => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
    } else if (!userProfile?.householdId) {
      setShowHouseholdSetup(true);
    }
  }, [isAuthenticated, userProfile?.householdId]);

  const handleLogout = useCallback(async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  }, [logout, navigate]);

  const handleAdminClick = useCallback(() => {
    setShowUserMenu(false);
    navigate('/admin');
  }, [navigate]);

  const handleSecondaryNav = useCallback((path) => {
    setShowUserMenu(false);
    navigate(path);
  }, [navigate]);

  const handleResetTutorial = useCallback(() => {
    if (confirm('¿Querés reiniciar el tutorial desde el principio?')) {
      setShowUserMenu(false);
      navigate('/');
      setTimeout(() => { resetTutorial(); }, 100);
    }
  }, [navigate, resetTutorial]);

  const handleCloseLoginModal = useCallback(() => setShowLoginModal(false), []);
  const handleCloseHouseholdSetup = useCallback(() => setShowHouseholdSetup(false), []);
  const handleCloseUserMenu = useCallback(() => setShowUserMenu(false), []);
  const handleOpenHouseholdSetup = useCallback(() => {
    setShowHouseholdSetup(true);
    setShowUserMenu(false);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col font-sans text-gray-900 dark:text-gray-100">
      <LayoutHeader
        isAuthenticated={isAuthenticated}
        user={user}
        userProfile={userProfile}
        household={household}
        syncEnabled={syncEnabled}
        isSyncing={isSyncing}
        isAdmin={isAdmin}
        onSyncClick={handleSyncClick}
        onOpenUserMenu={() => setShowUserMenu(true)}
      />
      <main className="flex-1 max-w-7xl w-full mx-auto p-3 pb-20 animate-fadeIn overflow-y-auto">
        {children}
      </main>
      <BottomNavigation navItems={navItems} currentPath={location.pathname} />
      {showUserMenu && (
        <UserMenu
          user={user}
          userProfile={userProfile}
          isAdmin={isAdmin}
          syncEnabled={syncEnabled}
          currentPath={location.pathname}
          secondaryNav={secondaryNav}
          onClose={handleCloseUserMenu}
          onResetTutorial={handleResetTutorial}
          onNavigate={handleSecondaryNav}
          onAdminClick={handleAdminClick}
          onOpenHouseholdSetup={handleOpenHouseholdSetup}
          onLogout={handleLogout}
        />
      )}
      <LoginModal isOpen={showLoginModal} onClose={handleCloseLoginModal} />
      <HouseholdSetup isOpen={showHouseholdSetup} onClose={handleCloseHouseholdSetup} />
      <LayoutStyles />
    </div>
  );
};

// ─────────────────────────────────────────────
// COMPONENTES MEMOIZADOS — sin cambios vs v4.0
// ─────────────────────────────────────────────

const LayoutHeader = memo(function LayoutHeader({
  isAuthenticated, user, userProfile, household, syncEnabled, isSyncing, isAdmin, onSyncClick, onOpenUserMenu
}) {
  return (
    <header className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700/50 sticky top-0 z-30 safe-top shadow-md h-14">
      <div className="max-w-7xl mx-auto px-3 h-full relative">
        <div className="flex items-center justify-between h-full">
          <HeaderLogo />
          <HeaderStatus
            isAuthenticated={isAuthenticated}
            userProfile={userProfile}
            household={household}
            syncEnabled={syncEnabled}
            isSyncing={isSyncing}
            onSyncClick={onSyncClick}
          />
          <div className="flex items-center gap-2 flex-shrink-0 z-10">
            <ThemeToggle />
            {isAuthenticated && (
              <UserAvatar user={user} isAdmin={isAdmin} onOpenUserMenu={onOpenUserMenu} />
            )}
          </div>
        </div>
      </div>
    </header>
  );
});

const HeaderLogo = memo(function HeaderLogo() {
  return (
    <div className="flex items-center gap-2 flex-shrink-0 z-10">
      <Link to="/" className="flex items-center gap-2 group">
        <div className="w-8 h-8 rounded-xl overflow-hidden shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-all duration-300 group-hover:scale-105">
          <img src="/icon-192x192.png" alt="MealCycle" className="w-full h-full object-cover" />
        </div>
        <h1 className="text-base font-bold bg-gradient-to-r from-green-400 via-blue-400 to-blue-500 bg-clip-text text-transparent hidden sm:block group-hover:from-green-300 group-hover:via-blue-300 group-hover:to-blue-400 transition-all">
          MealCycle
        </h1>
      </Link>
    </div>
  );
});

const HeaderStatus = memo(function HeaderStatus({
  isAuthenticated, userProfile, household, syncEnabled, isSyncing, onSyncClick
}) {
  return (
    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full flex justify-center pointer-events-none">
      <div className="pointer-events-auto max-w-[140px] xs:max-w-[180px]">
        {!isAuthenticated ? (
          <button
            onClick={onSyncClick}
            className="bg-blue-600/90 backdrop-blur hover:bg-blue-700 active:scale-95 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg shadow-blue-500/20 transition flex items-center gap-1.5"
          >
            <span className="text-sm">👤</span>
            <span>Ingresar</span>
          </button>
        ) : !userProfile?.householdId ? (
          <button
            onClick={onSyncClick}
            className="bg-orange-100 dark:bg-orange-500/20 backdrop-blur-sm text-orange-700 dark:text-orange-400 border border-orange-300 dark:border-orange-500/50 px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-1.5 shadow-[0_0_15px_rgba(249,115,22,0.2)] active:scale-95 transition"
          >
            <span className="text-xs">⚠️</span>
            <span>Configurar</span>
          </button>
        ) : (
          <div className="flex items-center gap-1.5 bg-white/70 dark:bg-gray-900/60 backdrop-blur-md rounded-full pl-1.5 pr-2.5 py-1 border border-gray-200 dark:border-gray-700/50 shadow-lg">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              isSyncing ? 'bg-yellow-400 animate-spin' :
              syncEnabled ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]' : 'bg-gray-500'
            }`} />
            <span className="text-xs text-gray-700 dark:text-gray-200 font-medium truncate max-w-[80px] xs:max-w-[120px]">
              {household?.name || 'Mi Hogar'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

const UserAvatar = memo(function UserAvatar({ user, isAdmin, onOpenUserMenu }) {
  return (
    <div className="flex items-center flex-shrink-0 z-10">
      <button onClick={onOpenUserMenu} className="relative group active:scale-95 transition">
        {isAdmin && (
          <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/50 z-10">
            <span className="text-[8px]">👑</span>
          </div>
        )}
        <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-300 dark:border-gray-600 group-active:border-blue-500 transition shadow-md bg-gray-200 dark:bg-gray-700">
          {user?.photoURL ? (
            <img src={user.photoURL} alt="User" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center text-gray-800 dark:text-white text-xs font-bold">
              {user?.displayName?.charAt(0) || 'U'}
            </div>
          )}
        </div>
      </button>
    </div>
  );
});

const BottomNavigation = memo(function BottomNavigation({ navItems, currentPath }) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-t border-gray-200 dark:border-gray-700/50 pb-safe-bottom z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.12)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.3)]">
      <div className="max-w-7xl mx-auto flex justify-around items-center px-1 py-1.5">
        {navItems.map(item => (
          <NavItem key={item.path} item={item} isActive={currentPath === item.path} />
        ))}
      </div>
    </nav>
  );
});

const NavItem = memo(function NavItem({ item, isActive }) {
  return (
    <Link
      to={item.path}
      className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-xl transition-all duration-200 active:scale-95 relative ${
        isActive ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500'
      }`}
    >
      {isActive && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-green-400 to-blue-400 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
      )}
      <div className={`relative transition-transform duration-200 ${isActive ? 'scale-110' : ''}`}>
        <span className={`text-2xl ${isActive ? 'drop-shadow-[0_0_12px_rgba(59,130,246,0.6)]' : ''}`}>
          {item.icon}
        </span>
      </div>
      <span className={`text-[10px] font-medium mt-0.5 transition-opacity ${
        isActive ? 'opacity-100 font-bold' : 'opacity-70'
      }`}>
        {item.label}
      </span>
    </Link>
  );
});

const LayoutStyles = memo(function LayoutStyles() {
  return (
    <style>{`
      .safe-top { padding-top: env(safe-area-inset-top); }
      .pb-safe-bottom { padding-bottom: env(safe-area-inset-bottom); }
      .scrollbar-hide::-webkit-scrollbar { display: none; }
      .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
      .animate-slideUp { animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
      * { -webkit-tap-highlight-color: transparent; }
      button, a { touch-action: manipulation; }
    `}</style>
  );
});

const UserMenu = memo(function UserMenu({
  user, userProfile, isAdmin, syncEnabled, currentPath, secondaryNav,
  onClose, onResetTutorial, onNavigate, onAdminClick, onOpenHouseholdSetup, onLogout
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-start sm:justify-end sm:p-4 animate-fadeIn">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative w-full sm:w-80 bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl border-t sm:border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden animate-slideUp sm:animate-fadeIn">
        <div className="w-full flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-gray-400 dark:bg-gray-600 rounded-full opacity-50" />
        </div>
        <div className="p-5">
          <UserInfo user={user} isAdmin={isAdmin} syncEnabled={syncEnabled} />
          <TutorialButton onClick={onResetTutorial} />
          <SecondaryNavigation items={secondaryNav} currentPath={currentPath} onNavigate={onNavigate} />
          <ActionButtons
            isAdmin={isAdmin}
            hasHousehold={!!userProfile?.householdId}
            onAdminClick={onAdminClick}
            onOpenHouseholdSetup={onOpenHouseholdSetup}
            onLogout={onLogout}
          />
          <button onClick={onClose} className="w-full mt-5 py-2.5 text-center text-gray-500 dark:text-gray-500 text-sm font-medium hover:text-gray-900 dark:hover:text-white sm:hidden active:scale-95 transition">
            Cerrar menú
          </button>
        </div>
      </div>
    </div>
  );
});

const UserInfo = memo(function UserInfo({ user, isAdmin, syncEnabled }) {
  return (
    <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-200 dark:border-gray-700">
      <div className="relative">
        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-500 shadow-lg shadow-blue-500/20">
          {user?.photoURL ? (
            <img src={user.photoURL} alt="User" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold">
              {user?.displayName?.charAt(0) || 'U'}
            </div>
          )}
        </div>
        {isAdmin && (
          <div className="absolute -bottom-0.5 -right-0.5 px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full text-[9px] font-bold text-gray-900 shadow-lg">👑</div>
        )}
      </div>
      <div className="overflow-hidden flex-1">
        <p className="text-gray-900 dark:text-white text-base font-bold truncate">{user?.displayName}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
        <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
          <div className={`w-1.5 h-1.5 rounded-full ${syncEnabled ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className="text-[10px] text-gray-600 dark:text-gray-300 uppercase tracking-wider font-bold">
            {syncEnabled ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>
    </div>
  );
});

const TutorialButton = memo(function TutorialButton({ onClick }) {
  return (
    <div className="mb-4">
      <button
        onClick={onClick}
        className="w-full p-3 flex items-center gap-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 hover:from-purple-500/20 hover:to-blue-500/20 active:scale-95 rounded-xl text-purple-400 transition border border-purple-500/30 group shadow-lg shadow-purple-500/10"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-lg group-hover:scale-110 transition shadow-lg">🎓</div>
        <div className="text-left flex-1">
          <p className="text-sm font-bold text-gray-900 dark:text-white">Ver Tutorial de Nuevo</p>
          <p className="text-xs text-purple-300/70">Guía paso a paso</p>
        </div>
        <span className="text-purple-500">›</span>
      </button>
    </div>
  );
});

const SecondaryNavigation = memo(function SecondaryNavigation({ items, currentPath, onNavigate }) {
  return (
    <div className="mb-4 p-3 bg-gray-100/80 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700/50">
      <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-2 px-1">Más opciones</p>
      <div className="space-y-1">
        {items.map(item => (
          <SecondaryNavItem key={item.path} item={item} isActive={currentPath === item.path} onClick={() => onNavigate(item.path)} />
        ))}
      </div>
    </div>
  );
});

const SecondaryNavItem = memo(function SecondaryNavItem({ item, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full px-3 py-2 flex items-center gap-3 rounded-lg transition active:scale-95 ${
        isActive ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/30' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50'
      }`}
    >
      <span className="text-lg">{item.icon}</span>
      <span className="text-sm font-medium">{item.label}</span>
    </button>
  );
});

const ActionButtons = memo(function ActionButtons({
  isAdmin, hasHousehold, onAdminClick, onOpenHouseholdSetup, onLogout
}) {
  return (
    <div className="space-y-2">
      {isAdmin && (
        <button
          onClick={onAdminClick}
          className="w-full p-3 flex items-center gap-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 hover:from-yellow-500/20 hover:to-orange-500/20 active:scale-95 rounded-xl text-yellow-400 transition border border-yellow-500/30 group shadow-lg shadow-yellow-500/10"
        >
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-lg group-hover:scale-110 transition shadow-lg">👑</div>
          <div className="text-left flex-1">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Panel de Admin</p>
            <p className="text-xs text-yellow-300/70">Gestionar contenido</p>
          </div>
          <span className="text-yellow-500">›</span>
        </button>
      )}
      {hasHousehold ? (
        <button
          onClick={onOpenHouseholdSetup}
          className="w-full p-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-95 rounded-xl text-gray-700 dark:text-gray-200 transition border border-gray-300 dark:border-gray-700/50 group"
        >
          <div className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center text-lg group-hover:scale-110 transition">🏠</div>
          <div className="text-left flex-1">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Gestionar Hogar</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Ver código y miembros</p>
          </div>
          <span className="text-gray-500">›</span>
        </button>
      ) : (
        <button
          onClick={onOpenHouseholdSetup}
          className="w-full p-3 flex items-center gap-3 bg-orange-500/10 hover:bg-orange-500/20 active:scale-95 rounded-xl text-orange-400 transition border border-orange-500/30 animate-pulse"
        >
          <div className="w-9 h-9 rounded-full bg-orange-500/20 flex items-center justify-center text-lg">⚠️</div>
          <div className="text-left flex-1">
            <p className="text-sm font-bold">Configurar Hogar</p>
            <p className="text-xs text-orange-300/70">Es necesario para empezar</p>
          </div>
        </button>
      )}
      <button
        onClick={onLogout}
        className="w-full p-3 flex items-center gap-3 bg-red-500/5 hover:bg-red-500/10 active:scale-95 border border-red-500/20 rounded-xl text-red-400 transition mt-2"
      >
        <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center text-lg">🚪</div>
        <span className="text-sm font-medium">Cerrar Sesión</span>
      </button>
    </div>
  );
});

export default Layout;